"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { HeaderDathaconsProps } from "src/lib/utils/interfaces/components/header-dathacons";
import type { SocialGroupInterface } from "src/lib/utils/interfaces";
import { DathaconsHeaderTopBar } from "./dathacons/top-bar";
import { MobileMenu } from "./dathacons/MobileMenu";
import { toAssetImages } from "src/lib/utils";
import "./dathacons/dathacons-interactive.css";
import "./dathacons/dathacons-responsive.css";

function sectionIdFromTarget(target: string | undefined): string | undefined {
  if (!target) return undefined;
  return target.replace(/^#/, "");
}

function HeaderDathacons(props: HeaderDathaconsProps) {
  const {
    logoUrl,
    phone,
    email,
    nav = [],
    socialGroup = [],
    contactButtonLabel = "Liên hệ",
    contactScrollTarget = "contact",
  } = props;

  const pathname = usePathname();
  const router = useRouter();
  const [activeSection, setActiveSection] = useState<string | undefined>();

  const scrollToId = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get("scrollTo");
      if (targetId) {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", url.toString());
    }, 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => e.target.getAttribute("id"))
          .filter(Boolean) as string[];
        if (visible.length) setActiveSection(visible[0]);
      },
      { rootMargin: "0px 0px -50% 0px", threshold: 0.2 },
    );

    const els = nav
      .filter((item) => item.action === "scroll" && item.target)
      .map((item) => document.getElementById(sectionIdFromTarget(item.target)!))
      .filter((el): el is HTMLElement => !!el);

    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, [nav]);

  const onClickNav = (item: (typeof nav)[0]) => {
    if (item.action !== "scroll" || !item.target) return;
    const id = sectionIdFromTarget(item.target);
    if (!id) return;
    if (item.target === "/" && pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (pathname !== "/") {
      router.push(`/?scrollTo=${id}`);
      return;
    }
    scrollToId(id);
  };

  const firstScrollSectionId = sectionIdFromTarget(nav.find((i) => i.action === "scroll")?.target);

  const isNavActive = (sectionId: string | undefined) => {
    if (activeSection === sectionId) return true;
    if (activeSection === undefined && sectionId && sectionId === firstScrollSectionId) return true;
    return false;
  };

  const mobileNav = nav
    .filter((i) => i.action === "scroll" && i.label && i.target)
    .map((i) => ({
      label: i.label!,
      sectionId: sectionIdFromTarget(i.target) ?? "",
    }))
    .filter((i) => i.sectionId);

  const sg = socialGroup as SocialGroupInterface[];

  return (
    <div
      className="bg-white content-stretch flex flex-col items-start sticky top-0 z-50 shadow-[0px_4px_26px_0px_rgba(0,0,0,0.06)] shrink-0 w-full"
      data-name="Header"
    >
      <DathaconsHeaderTopBar phone={phone} email={email} socialGroup={sg} />

      <div className="relative shrink-0 w-full" data-name="Header middle row">
        <div className="flex flex-col items-center justify-center size-full">
          <div className="content-stretch flex flex-col items-center justify-center px-[10px] py-[16px] relative w-full">
            <div
              className="content-stretch flex items-center justify-between relative shrink-0 w-[1120px] max-w-full gap-3"
              data-name="Container max-width 1120px"
            >
              <button
                type="button"
                onClick={() => scrollToId("hero")}
                className="relative shrink-0 border-0 bg-transparent p-0 cursor-pointer grid-cols-[max-content] grid-rows-[max-content] inline-grid leading-[0] place-items-start"
                data-name="Logo container"
                aria-label="Về đầu trang"
              >
                <img
                  src={logoUrl ? toAssetImages(logoUrl) : undefined}
                  alt=""
                  width={220}
                  height={64}
                  decoding="async"
                  className="h-16 w-auto max-w-[min(240px,40vw)] object-contain object-left pointer-events-none"
                />
              </button>

              <nav
                className="content-stretch flex gap-[26px] items-center leading-[normal] not-italic relative shrink-0 text-[16px] whitespace-nowrap"
                data-name="Menu items"
                aria-label="Chính"
              >
                {nav.map((item, idx) => {
                  if (item.action !== "scroll" || !item.label) return null;
                  const sid = sectionIdFromTarget(item.target);
                  const active = isNavActive(sid);
                  return (
                    <button
                      key={`${item.label}-${idx}`}
                      type="button"
                      className={
                        active
                          ? "font-semibold relative shrink-0 text-[#ffa411] cursor-pointer bg-transparent border-0 p-0"
                          : "font-normal relative shrink-0 text-[#303030] cursor-pointer hover:text-[#ffa411] transition-colors bg-transparent border-0 p-0"
                      }
                      onClick={() => onClickNav(item)}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </nav>

              <div className="content-stretch flex items-center gap-2 sm:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => scrollToId(contactScrollTarget)}
                  className="bg-[#ffa411] content-stretch flex items-center justify-center px-[16px] py-[10px] relative rounded-[8px] shrink-0 border-0 cursor-pointer"
                  data-name="Contact button"
                >
                  <span className="font-medium leading-[normal] not-italic relative shrink-0 text-[16px] text-white whitespace-nowrap">
                    {contactButtonLabel}
                  </span>
                </button>
                <MobileMenu
                  phone={phone}
                  email={email}
                  nav={mobileNav}
                  activeSectionId={activeSection}
                  onNavigate={scrollToId}
                  onContact={() => scrollToId(contactScrollTarget)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

HeaderDathacons.displayName = "HeaderDathacons";

export default HeaderDathacons;
